import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  ActiveCheckoutRecommendationRule,
  CheckoutRecommendationItem,
} from './customer-checkout-recommendations.helpers';

/**
 * Customer-side HTTP service for the Checkout Recommendations public APIs (#9303).
 *
 * Both endpoints are annotated with `@PublicAPI` on the backend and live under
 * `/api/v1/stores/{storeId}/checkout-recommendation-rules`. The customer storefront calls them
 * just-in-time as the recommendation flow advances through one page per active rule.
 */
@Injectable({
  providedIn: 'root',
})
export class CustomerCheckoutRecommendationsService {
  constructor(private readonly http: HttpClient) {}

  /**
   * List of active rules in admin-defined order, with each rule's question already translated
   * server-side. Returns an empty list when the store-level master toggle is off — the customer
   * UI uses the empty list as the signal to skip the entire recommendation step.
   */
  loadActiveRules(
    storeId: number,
    languageId?: number,
  ): Observable<ActiveCheckoutRecommendationRule[]> {
    let params = new HttpParams();
    if (languageId != null) {
      params = params.set('languageId', languageId.toString());
    }
    return this.http.get<ActiveCheckoutRecommendationRule[]>(
      `/api/v1/stores/${storeId}/checkout-recommendation-rules/active`,
      { params },
    );
  }

  /**
   * Resolve recommended items for one rule against the current basket. The caller passes the
   * set of categories already represented in the basket; the backend filters its eligible
   * categories down accordingly and applies the round-robin selection.
   *
   * Returns an empty list when nothing should be shown — the customer UI uses that as the
   * signal to silently skip the rule's page and advance to the next one (or to checkout).
   */
  resolveItems(
    storeId: number,
    ruleId: number,
    basketCoveredCategoryIds: number[],
  ): Observable<CheckoutRecommendationItem[]> {
    let params = new HttpParams();
    if (basketCoveredCategoryIds && basketCoveredCategoryIds.length > 0) {
      params = params.set(
        'basketCoveredCategoryIds',
        basketCoveredCategoryIds.join(','),
      );
    }
    return this.http.get<CheckoutRecommendationItem[]>(
      `/api/v1/stores/${storeId}/checkout-recommendation-rules/${ruleId}/items`,
      { params },
    );
  }
}
